import { t as __t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import {
    Activity,
    AlertTriangle,
    Copy,
    Database,
    Eye,
    KeyRound,
    Loader2,
    MemoryStick,
    Plug,
    RefreshCw,
    Search,
    ShieldAlert,
    TimerReset,
    Trash2,
    X,
} from "lucide-react";
import "./index.css";
import { useDispatch } from "react-redux";
import { callGetRedisByKey, callGetRedisInfo, callGetRedisInfoByKey, callRedisDeleteByKey, callRedisFlushDB, callRedisUpdateTimeByKey } from "../../api/redis/service";
import DashboardHeader from "../../components/header/DashboardHeader";
import MobileMenu from "../../components/header/MobileMenu";
import DashboardEmployerSidebar from "../../components/header/DashboardSidebar";
import CopyrightFooter from "../../components/footer/employers/CopyrightFooter";
import SwalAlert from "../../components/common/alert";
import { useTranslation } from "react-i18next";
import SmartTable from "../../components/common/table-component";

export default function RedisControlPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [info, setInfo] = useState(null);
    const [keys, setKeys] = useState([]);
    const [pattern, setPattern] = useState("*");
    const [cursor, setCursor] = useState("0");
    const [nextCursor, setNextCursor] = useState("0");
    const [count, setCount] = useState(50);

    const [selectedKey, setSelectedKey] = useState(null);
    const [keyDetail, setKeyDetail] = useState(null);

    const [loadingInfo, setLoadingInfo] = useState(false);
    const [loadingKeys, setLoadingKeys] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const [ttlValue, setTtlValue] = useState("");
    const [message, setMessage] = useState(null);

    const hasMore = nextCursor !== "0";

    const prettyValue = useMemo(() => {
        if (!keyDetail?.value && keyDetail?.value !== 0) return "";

        if (typeof keyDetail.value === "string") {
            return keyDetail.value;
        }

        return JSON.stringify(keyDetail.value, null, 2);
    }, [keyDetail]);

    useEffect(() => {
        loadDashboard();
    }, []);

    const showMessage = (type, text) => {
        setMessage({ type, text });

        setTimeout(() => {
            setMessage(null);
        }, 3500);
    };

    const request = async (url, options = {}) => {
        // const res = await fetch(`${API_URL}${url}`, {
        //     ...options,
        //     headers: {
        //         ...headers,
        //         ...(options.headers || {}),
        //     },
        // });

        // const data = await res.json().catch(() => null);

        // if (!res.ok) {
        //     throw new Error(data?.message || "API request failed");
        // }

        // return ;
    };

    const loadDashboard = async () => {
        await Promise.all([loadInfo(), loadKeys("0", true)]);
    };

    const loadInfo = async () => {
        try {
            setLoadingKeys(true);
            // const res = await request("/redis/info");
            const rsp = await dispatch(callGetRedisInfo());
            if (rsp?.payload?.responseCode === "00000") {
                setInfo(rsp?.payload?.data);
            }
        } catch (error) {
            SwalAlert.error(__t("uiText.text023"), error.message);
        } finally {
            setLoadingKeys(false)
            setLoadingInfo(false);
        }
    };

    const loadKeys = async (customCursor = "0", reset = true, patternInput) => {
        try {
            setLoadingKeys(true);

            const query = new URLSearchParams({
                pattern: patternInput || pattern || "*",
                cursor: customCursor,
                count: String(count || 50),
            });
            const rsp = await dispatch(callGetRedisInfoByKey(query));
            if (rsp?.payload?.responseCode === "00000") {
                setNextCursor(rsp?.payload?.data?.cursor);
                if (reset) {
                    setKeys(rsp?.payload?.data?.items || []);
                    setCursor("0");
                } else {
                    setKeys((prev) => [...prev, ...(rsp?.payload?.data?.items || [])]);
                    setCursor(customCursor);
                }
            }
        } catch (error) {
            SwalAlert.error(__t("uiText.text023"), error.message);
        } finally {
            setLoadingKeys(false);
        }
    };

    const scanNow = async () => {
        setSelectedKey(null);
        setKeyDetail(null);
        await loadKeys("0", true);
    };

    const loadMore = async () => {
        if (!hasMore) return;
        await loadKeys(nextCursor, false);
    };

    const viewKey = async (item) => {
        try {
            setSelectedKey(item);
            setKeyDetail(null);
            setTtlValue("");
            setLoadingDetail(true);
            const query = new URLSearchParams({
                key: item.key,
            });
            const rsp = await dispatch(callGetRedisByKey(query));
            if (rsp?.payload?.responseCode === "00000") {
                setKeyDetail(rsp?.payload?.data);
                setTtlValue(rsp?.payload?.data.ttl > 0 ? String(rsp?.payload?.data.ttl) : "");
            }
        } catch (error) {
            SwalAlert.error(__t("uiText.text023"), error.message);
        } finally {
            setLoadingDetail(false);
        }
    };

    const deleteKey = async (key) => {
        SwalAlert.confirm(t("nameDelete"), __t("uiText.text311", { value1: key }), async () => {
            try {
                setActionLoading(true);

                const query = new URLSearchParams({ key });

                const rsp = await dispatch(callRedisDeleteByKey(query));
                if (rsp?.payload?.responseCode === "00000") {
                    setKeys((prev) => prev.filter((item) => item.key !== key));
                    if (selectedKey?.key === key) {
                        setSelectedKey(null);
                        setKeyDetail(null);
                    }
                    SwalAlert.successMSG(__t("uiText.text312"))

                }
                await loadInfo();
            } catch (error) {
                SwalAlert.error(__t("uiText.text023"), error.message);
            } finally {
                setActionLoading(false);
            }
        })
    };

    const updateTTL = async () => {
        if (!keyDetail?.key) return;
        const ttl = Number(ttlValue);
        if (!ttl || ttl < 1) {
            SwalAlert.error(__t("uiText.text023"), __t("uiText.text313"));
            return;
        }
        try {
            setActionLoading(true);
            const rsp = await dispatch(callRedisUpdateTimeByKey({
                key: keyDetail.key,
                ttl,
            }));
            if (rsp?.payload?.responseCode === "00000") {
                await viewKey({ key: keyDetail.key });
                await loadKeys("0", true);
                SwalAlert.successMSG(__t("uiText.text314"))

            }
        } catch (error) {
            SwalAlert.error(__t("uiText.text023"), error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const flushDb = async () => {
        SwalAlert.confirmInput(t("nameFlush"), __t("uiText.text315"), true, async (input) => {
            if (input !== "FLUSH_CURRENT_DB") {
                SwalAlert.error(__t("uiText.text023"), __t("uiText.text316"));
                return;
            }

            try {
                setActionLoading(true);

                await request("/admin/redis/flush-db", {
                    method: "POST",
                    body: JSON.stringify(),
                });
                const rsp = await dispatch(callRedisFlushDB({
                    confirm: "FLUSH_CURRENT_DB",
                }));
                if (rsp?.payload?.responseCode === "00000") {
                    SwalAlert.successMSG(__t("uiText.text317"))
                    setKeys([]);
                    setSelectedKey(null);
                    setKeyDetail(null);
                    await loadInfo();
                }


            } catch (error) {
                SwalAlert.error(__t("uiText.text023"), error.message);
            } finally {
                setActionLoading(false);
            }
        })
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showMessage("success", __t("uiText.text318"));
        } catch {
            showMessage("error", __t("uiText.text319"));
        }
    };

    const refreshSelectedKey = async () => {
        if (!selectedKey?.key) return;
        await viewKey(selectedKey);
    };

    return (
        <div className="page-wrapper dashboard">
            <span className="header-span"></span>
            <DashboardHeader />
            <MobileMenu />
            <DashboardEmployerSidebar />
            <section className="user-dashboard">
                <main className="redis-main">
                    <header className="redis-header">
                        <div>
                            <h1>{__t("uiText.text382")}</h1>
                            <p>{__t("uiText.text320")}</p>
                        </div>

                        <div className="redis-header-actions">
                            <button
                                className="btn btn-light"
                                onClick={loadDashboard}
                                disabled={loadingInfo || loadingKeys}
                            >
                                {loadingInfo || loadingKeys ? (
                                    <Loader2 className="spin" size={16} />
                                ) : (
                                    <RefreshCw size={16} />
                                )}
                                {__t("uiText.text429")}</button>

                            <button
                                className="btn btn-danger-soft"
                                onClick={flushDb}
                                disabled={actionLoading}
                            >
                                <AlertTriangle size={16} />
                                {__t("uiText.text383")}</button>
                        </div>
                    </header>

                    <section className="redis-summary-grid">
                        <SummaryCard
                            icon={<Database size={22} />}
                            title={__t("uiText.text384")}
                            value={loadingInfo ? "..." : info?.totalKeys ?? 0}
                            sub={`DB ${info?.db ?? 0}`}
                            tone="blue"
                        />

                        <SummaryCard
                            icon={<MemoryStick size={22} />}
                            title={__t("uiText.text385")}
                            value={loadingInfo ? "..." : info?.memoryUsed || "-"}
                            sub={`Peak ${info?.memoryPeak || "-"}`}
                            tone="orange"
                        />

                        <SummaryCard
                            icon={<Activity size={22} />}
                            title={__t("uiText.text386")}
                            value={loadingInfo ? "..." : info?.hitRate || "0%"}
                            sub={`Hit ${info?.keyspaceHits ?? 0} / Miss ${info?.keyspaceMisses ?? 0
                                }`}
                            tone="green"
                        />

                        <SummaryCard
                            icon={<Plug size={22} />}
                            title="Connections"
                            value={loadingInfo ? "..." : info?.connectedClients ?? 0}
                            sub={`Redis ${info?.redisVersion || "-"}`}
                            tone="purple"
                        />
                    </section>

                    <section className="redis-content">
                        <div className="redis-panel redis-table-panel">
                            <div className="panel-title-row">
                                <div>
                                    <h2>{__t("uiText.text387")}</h2>
                                    <p>{__t("uiText.text321")}</p>
                                </div>
                            </div>

                            <div className="redis-toolbar">
                                <div className="redis-search">
                                    <Search size={18} />
                                    <input
                                        value={pattern}
                                        onChange={(e) => setPattern(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") scanNow();
                                        }}
                                        placeholder={__t("uiText.text388")}
                                    />
                                </div>

                                <select
                                    value={count}
                                    onChange={(e) => setCount(Number(e.target.value))}
                                >
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                </select>

                                <button
                                    className="btn btn-primary"
                                    onClick={scanNow}
                                    disabled={loadingKeys}
                                >
                                    {loadingKeys ? <Loader2 className="spin" size={16} /> : null}
                                    {__t("uiText.text430")}</button>
                            </div>

                            <div className="quick-groups">
                                {["*", "product:*", "province:*", "district:*"].map(
                                    (item) => (
                                        <button
                                            key={item}
                                            className={pattern === item ? "active" : ""}
                                            onClick={() => {
                                                setPattern(item);
                                                setTimeout(() => loadKeys("0", true, item), 0);
                                            }}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            </div>

                            <div className="redis-table-wrap">
                                {loadingKeys && keys.length === 0 ? (
                                    <div className="empty-cell">
                                        <Loader2 className="spin" size={20} />
                                        {__t("uiText.text322")}</div>
                                ) : (
                                    <SmartTable
                                        columns={[
                                            { labelKey: "redis.labels.key", key: "key", render: (val, row) => <button className="key-name" onClick={() => viewKey(row)}>{val}</button> },
                                            { labelKey: "redis.labels.type", key: "type", render: (val) => <span className="type-badge">{val}</span> },
                                            { labelKey: "redis.labels.ttl", key: "ttl", render: (_, row) => <TTLBadge ttl={row.ttl} ttlText={row.ttlText} /> },
                                            { labelKey: "redis.labels.size", key: "sizeText" },
                                        ]}
                                        data={{ result: keys }}
                                        rowKey="key"
                                        recordsTableFilter={false}
                                        customPagination={false}
                                        showRowNumber={false}
                                        rowClassName={(row) => selectedKey?.key === row.key ? "selected-row" : ""}
                                        actions={(item) => (
                                            <div>
                                                <button className="icon-btn" onClick={() => viewKey(item)} title="View"><Eye size={16} /></button>
                                                <button className="icon-btn" onClick={() => copyText(item.key)} title="Copy"><Copy size={16} /></button>
                                                <button className="icon-btn danger" onClick={() => deleteKey(item.key)} title="Delete"><Trash2 size={16} /></button>
                                            </div>
                                        )}
                                    />
                                )}
                            </div>

                            <div className="table-footer">
                                <div>
                                    {__t("uiText.text431")}<b>{keys.length}</b> {__t("uiText.text432")}{hasMore ? " · more available" : ""}
                                </div>

                                <button
                                    className="btn btn-light"
                                    onClick={loadMore}
                                    disabled={!hasMore || loadingKeys}
                                >
                                    {loadingKeys ? <Loader2 className="spin" size={16} /> : null}
                                    {__t("uiText.text389")}</button>
                            </div>
                        </div>

                        <aside className="redis-panel redis-detail-panel">
                            {!selectedKey ? (
                                <div className="detail-empty">
                                    <Database size={46} />
                                    <h3>{__t("uiText.text323")}</h3>
                                    <p>{__t("uiText.text324")}</p>
                                </div>
                            ) : (
                                <>
                                    <div className="detail-header">
                                        <div>
                                            <h2>{__t("uiText.text390")}</h2>
                                            <p>{__t("uiText.text325")}</p>
                                        </div>

                                        <button
                                            className="close-btn"
                                            onClick={() => {
                                                setSelectedKey(null);
                                                setKeyDetail(null);
                                            }}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>

                                    {loadingDetail ? (
                                        <div className="detail-loading">
                                            <Loader2 className="spin" size={24} />
                                            {__t("uiText.text326")}</div>
                                    ) : keyDetail ? (
                                        <>
                                            <div className="detail-box">
                                                <label>{t("redis.labels.key")}</label>
                                                <div className="code-line">{keyDetail.key}</div>
                                            </div>

                                            <div className="detail-grid">
                                                <div className="detail-box">
                                                    <label>{t("redis.labels.type")}</label>
                                                    <span className="type-badge">{keyDetail.type}</span>
                                                </div>

                                                <div className="detail-box">
                                                    <label>{t("redis.labels.ttl")}</label>
                                                    <TTLBadge
                                                        ttl={keyDetail.ttl}
                                                        ttlText={keyDetail.ttlText}
                                                    />
                                                </div>
                                            </div>

                                            <div className="detail-grid">
                                                <div className="detail-box">
                                                    <label>{t("redis.labels.size")}</label>
                                                    <b>{keyDetail.sizeText || "-"}</b>
                                                </div>

                                                <div className="detail-box">
                                                    <label>{t("redis.labels.refresh")}</label>
                                                    <button
                                                        className="mini-action"
                                                        onClick={refreshSelectedKey}
                                                    >
                                                        <RefreshCw size={14} />
                                                        {__t("uiText.text433")}</button>
                                                </div>
                                            </div>

                                            <div className="ttl-update-box">
                                                <label>{t("redis.labels.updateTtlSeconds")}</label>
                                                <div className="ttl-row">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={ttlValue}
                                                        onChange={(e) => setTtlValue(e.target.value)}
                                                        placeholder="300"
                                                    />
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={updateTTL}
                                                        disabled={actionLoading}
                                                    >
                                                        <TimerReset size={16} />
                                                        {__t("uiText.text434")}</button>
                                                </div>
                                            </div>

                                            <div className="value-header">
                                                <label>{t("redis.labels.value")}</label>

                                                <button
                                                    className="mini-action"
                                                    onClick={() => copyText(prettyValue)}
                                                >
                                                    <Copy size={14} />
                                                    {__t("uiText.text435")}</button>
                                            </div>

                                            <pre className="value-preview">{prettyValue}</pre>

                                            <div className="detail-actions">
                                                <button
                                                    className="btn btn-light"
                                                    onClick={() => copyText(keyDetail.key)}
                                                >
                                                    <Copy size={16} />
                                                    {__t("uiText.text391")}</button>

                                                <button
                                                    className="btn btn-danger"
                                                    onClick={() => deleteKey(keyDetail.key)}
                                                    disabled={actionLoading}
                                                >
                                                    <Trash2 size={16} />
                                                    {__t("uiText.text392")}</button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="detail-empty">
                                            <AlertTriangle size={42} />
                                            <h3>{__t("uiText.text327")}</h3>
                                        </div>
                                    )}
                                </>
                            )}
                        </aside>
                    </section>
                </main>
            </section>
            <CopyrightFooter />
        </div>

    );
}

function SummaryCard({ icon, title, value, sub, tone }) {
    return (
        <div className="summary-card">
            <div className={`summary-icon ${tone}`}>{icon}</div>
            <div>
                <p>{title}</p>
                <h3>{value}</h3>
                <span>{sub}</span>
            </div>
        </div>
    );
}

function TTLBadge({ ttl, ttlText }) {
    let className = "ttl-badge";

    if (ttl === -1) className += " no-expire";
    else if (ttl === -2) className += " expired";
    else if (ttl >= 0 && ttl <= 60) className += " danger";
    else className += " active";

    return <span className={className}>{ttlText}</span>;
}
